import { Button } from '@/design-system/button/button';
import { DropdownMenuNew } from '@/design-system/dropdown-menu-new/dropdown-menu-new';
import { Inline } from '@/design-system/inline/inline';
import { DropdownMenuGroup, DropdownMenuItem } from '@/design-system/ui/dropdown-menu';
import { exhaustiveCheck } from '@/utils/exhaustive-check';
import { tw } from '@/utils/tw';
import { FileText, HelpCircle, Info, MoreHorizontal } from 'lucide-react';

type MenuItemVariant = 'INVESTOR_INFORMATION' | 'MORE_ABOUT_THE_FUND' | 'FAQS';

type MenuItem = {
  variant: MenuItemVariant;
  onSelect: () => void;
};

type MoreInfoDropdownMenuProps = {
  menuItems: MenuItem[];
};

export const MoreInfoDropdownMenu = ({ menuItems }: MoreInfoDropdownMenuProps) => {
  return (
    <DropdownMenuNew
      trigger={
        <Button variant="ghost" size="icon" className="bg-secondary">
          <MoreHorizontal className="h-4 w-4 text-secondary-foreground" />
        </Button>
      }
    >
      <DropdownMenuGroup>
        {menuItems.map((item) => {
          const { icon, label } = getMenuItemContent(item.variant);

          return (
            <DropdownMenuItem key={item.variant} onSelect={item.onSelect}>
              <Inline space={2}>
                {icon}

                <p className="truncate text-sm leading-5 font-normal text-popover-foreground">{label}</p>
              </Inline>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuGroup>
    </DropdownMenuNew>
  );
};

const getMenuItemContent = (variant: MenuItemVariant) => {
  const iconClassName = tw`h-4 w-4 shrink-0 text-muted-foreground`;

  switch (variant) {
    case 'INVESTOR_INFORMATION':
      return { icon: <FileText className={iconClassName} />, label: 'Key investor information' };
    case 'MORE_ABOUT_THE_FUND':
      return { icon: <Info className={iconClassName} />, label: 'More about the fund' };
    case 'FAQS':
      return { icon: <HelpCircle className={iconClassName} />, label: 'View FAQs' };
    default:
      return exhaustiveCheck(variant);
  }
};
